// ─────────────────────────────────────────────────────────────────────────────
// Scénarios de masse salariale — fonctions pures, importables côté client
// (aucun Firestore). Un scénario est une liste de membres positionnés sur la
// grille (classe + échelon) : membres réels de l'équipe, déplacés ou non, plus
// d'éventuels employés fictifs. Rien n'est persisté.
// Les taux sont projetés vers l'année courante via aug_grille_pct, comme la
// frise salariale (cohérence avec le reste de l'app).
// ─────────────────────────────────────────────────────────────────────────────

import { annualFromHourly, projectYearRate } from './projection'
import { GRID_YEAR } from './progression'
import type { SalaryClass } from '../types/firestore'

export type ScenarioMember = {
  // Email pour un membre réel, identifiant local pour un fictif.
  id: string
  name: string
  class_id: string | null
  echelon: number
  virtual: boolean
  // Hors grille : la paie suit le taux réel, pas la position classe/échelon —
  // exclu de la masse GRILLE (signalé à part) ; le coût réel le couvre.
  offGrid?: boolean
}

// Équivalent annuel d'un positionnement classe × échelon. null si la classe ou
// l'échelon n'existe pas (rémunération non configurée).
export function positionAnnual(
  classId: string | null,
  echelon: number,
  classes: SalaryClass[],
  currentYear: number,
  gridYear: number = GRID_YEAR,
): number | null {
  if (!classId) return null
  const cls = classes.find((c) => c.id === classId)
  if (!cls) return null
  const ech = cls.echelons.find((e) => e.echelon === echelon)
  if (!ech) return null
  const rate = projectYearRate(ech.rate, currentYear - gridYear, cls.aug_grille_pct)
  return annualFromHourly(rate, {
    base_hours_per_week: cls.base_hours_per_week,
    weeks_per_year: cls.weeks_per_year,
  })
}

// Taux horaire projeté d'un positionnement classe × échelon (même projection que
// positionAnnual, pour un affichage cohérent — ex. infobulle de la grille). null
// si la classe/échelon n'existe pas.
export function positionHourly(
  classId: string | null,
  echelon: number,
  classes: SalaryClass[],
  currentYear: number,
  gridYear: number = GRID_YEAR,
): number | null {
  if (!classId) return null
  const cls = classes.find((c) => c.id === classId)
  if (!cls) return null
  const ech = cls.echelons.find((e) => e.echelon === echelon)
  if (!ech) return null
  return projectYearRate(ech.rate, currentYear - gridYear, cls.aug_grille_pct)
}

export type PayrollTotals = {
  total: number
  // Membres dont la position ne donne pas de montant (non configurés) —
  // exclus du total, à signaler à l'affichage.
  unpriced: string[]
  // Membres HORS GRILLE — exclus de la masse GRILLE (leur échelon est périmé,
  // leur paie suit le taux réel) ; comptés dans le coût réel (realCostTotals).
  offGrid: string[]
}

export function payrollTotals(
  members: ScenarioMember[],
  classes: SalaryClass[],
  currentYear: number,
  gridYear: number = GRID_YEAR,
): PayrollTotals {
  let total = 0
  const unpriced: string[] = []
  const offGrid: string[] = []
  for (const m of members) {
    if (m.offGrid) {
      offGrid.push(m.id)
      continue
    }
    const annual = positionAnnual(m.class_id, m.echelon, classes, currentYear, gridYear)
    if (annual === null) unpriced.push(m.id)
    else total += annual
  }
  return { total, unpriced, offGrid }
}

export type ScenarioDelta = {
  baseline: number
  scenario: number
  deltaAnnual: number
  deltaPct: number | null // null si la masse de départ est 0
}

export function scenarioDelta(baseline: number, scenario: number): ScenarioDelta {
  const delta = scenario - baseline
  return {
    baseline,
    scenario,
    deltaAnnual: delta,
    deltaPct: baseline > 0 ? delta / baseline : null,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Coût RÉEL d'un scénario — réconcilie la planification (grille) avec la masse
// salariale réellement budgétée. La masse grille (payrollTotals ci-dessus) sert
// l'équité interne ; ce calcul-ci donne l'impact BUDGET en partant du coût réel
// actuel de chaque employé (salaire réel prorata + avantages employeur + boni)
// plutôt que du seul taux de grille.
//
// Hypothèse de projection : un membre DÉPLACÉ est budgété au taux de grille de sa
// nouvelle position (l'intention d'une promotion) ; un membre non déplacé garde
// son coût réel actuel ; les avantages/boni (extras) suivent l'employé inchangés ;
// un employé fictif est budgété au taux de grille (aucun avantage connu).
// ─────────────────────────────────────────────────────────────────────────────

export type MemberRealCost = {
  // Salaire annuel réel (prorata des taux datés) ; null si aucun taux connu —
  // on retombe alors sur le taux de grille de la position.
  realBase: number | null
  // Charge récurrente annuelle qui suit l'employé : avantages employeur + boni
  // + supplément d'heures spéciales.
  extras: number
}

export type RealScenarioMember = {
  id: string // email (réel) ou identifiant local (fictif)
  before: { class_id: string | null; echelon: number } | null // null pour un fictif
  after: { class_id: string | null; echelon: number }
  virtual: boolean
  // Hors grille : jamais « déplacé » (l'UI l'empêche) — défense en profondeur,
  // son coût reste sa base réelle même si une position after divergente traîne.
  offGrid?: boolean
}

export type RealCostTotals = {
  baseline: number // masse réelle actuelle (base réelle + extras)
  scenario: number // masse projetée (base grille si déplacé, sinon base réelle)
  deltaAnnual: number
  deltaPct: number | null
  charge: number // total des extras (avantages + boni + heures spéc.)
}

export function realCostTotals(
  members: RealScenarioMember[],
  realById: Record<string, MemberRealCost>,
  classes: SalaryClass[],
  currentYear: number,
  gridYear: number = GRID_YEAR,
): RealCostTotals {
  let baseline = 0
  let scenario = 0
  let charge = 0
  for (const m of members) {
    if (m.virtual) {
      scenario +=
        positionAnnual(m.after.class_id, m.after.echelon, classes, currentYear, gridYear) ??
        0
      continue
    }
    const rc = realById[m.id] ?? { realBase: null, extras: 0 }
    const before = m.before ?? m.after
    const moved =
      !m.offGrid &&
      (m.before == null ||
        before.class_id !== m.after.class_id ||
        before.echelon !== m.after.echelon)
    const gridBefore =
      positionAnnual(before.class_id, before.echelon, classes, currentYear, gridYear) ?? 0
    const baseBefore = rc.realBase ?? gridBefore
    const baseAfter = moved
      ? (positionAnnual(m.after.class_id, m.after.echelon, classes, currentYear, gridYear) ??
        baseBefore)
      : baseBefore
    baseline += baseBefore + rc.extras
    scenario += baseAfter + rc.extras
    charge += rc.extras
  }
  const delta = scenario - baseline
  return {
    baseline,
    scenario,
    deltaAnnual: delta,
    deltaPct: baseline > 0 ? delta / baseline : null,
    charge,
  }
}
